/**
 * A learner's own AI agent (docs/agents.md): the agent keys on the agent page
 * (components/agent/AgentKeys.tsx, `/agent`) and the strip on the play screen that lets
 * the agent use this tab (components/play/AgentStrip.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the glossary ("the agent" is الوكيل, der Agent,
 * エージェント). MCP, Claude Code, Codex and Cursor are names and stay Latin.
 *
 * The register: the agent acts as the learner and the work is theirs to
 * stand behind. Nothing here promises earnings, and nothing says an agent can
 * do a task on its own.
 *
 * When paid tasks retired on 2026-10-31 the intro and limits lost their
 * clauses about claiming and delivering tasks and spending credits, in every
 * language, by deletion only: nothing new was written to translate.
 */
import { shortDate } from '../format';

/** What the tab did for the agent, one line each on the strip: this verb, then what it was done to. */
export type AgentDid = 'run' | 'read_file' | 'write_file' | 'list_files' | 'check' | 'open' | 'status' | 'levels';

export const en = {
  // The play screen.
  connect: 'Let my agent use this tab',
  connecting: 'Connecting your agent…',
  connected: 'Your agent can use this tab',
  stop: 'Stop',
  takenOver: 'Your agent now uses another tab.',
  lost: 'Lost the connection to your agent. Trying again…',
  explain:
    'Your own AI agent, connected with an agent key, can run commands in this machine, edit the level\'s files and press Check my work. Each thing it does is listed here.',
  setUp: 'Set up your agent',
  did: { run: 'Ran', read_file: 'Read', write_file: 'Wrote', list_files: 'Listed', check: 'Pressed Check my work', open: 'Opened', status: 'Looked at the level', levels: 'Looked at the level list' } as Record<AgentDid, string>,

  // The agent page (components/agent/AgentPage.tsx, its own copy in strings/agent-page.ts).
  title: 'Use your own agent',
  intro:
    'Any AI agent that speaks MCP (Claude Code, Codex, Cursor and others) can work on EngineerOS as you: do levels and checkpoints in a tab you connect. It acts as you, so the work is yours to stand behind.',
  limits:
    'A key can use a tab you connect. It can never change your account or speak in the chat.',
  server: 'Server address',
  forClaude: 'Add it to Claude Code',
  measure: 'Let us measure what it costs (Claude Code)',
  measureHint: 'Put this in .claude/settings.json in the folder you run Claude Code from for EngineerOS. While your agent has a level, checkpoint or task open, Claude Code reports each model call to us: the model, the token counts and the cost, so the level can show what your agent spent and how to spend less. Nothing else it sends is kept, never your prompts, and usage while nothing is open is dropped.',
  nameLabel: 'What the key is for',
  namePlaceholder: 'Claude Code on my laptop',
  make: 'Make a key',
  shownOnce: 'Copy this key now. It is not shown again.',
  copy: 'Copy',
  copied: 'Copied',
  revoke: 'Revoke',
  revoked: (at: number) => `Revoked ${shortDate(new Date(at), 'en')}`,
  made: (at: number) => `Made ${shortDate(new Date(at), 'en')}`,
  lastUsed: (at: number) => `last used ${shortDate(new Date(at), 'en')}`,
  neverUsed: 'not used yet',
  none: 'No keys yet.',
  tooMany: (max: number) => `You have ${max} keys. Revoke one to make another.`,
  failed: 'That did not work. Try again.',
  signIn: 'Sign in to make a key.',
};

export const ar: typeof en = {
  connect: 'اسمح لوكيلي باستخدام هذا التبويب',
  connecting: 'جارٍ توصيل وكيلك…',
  connected: 'يستطيع وكيلك استخدام هذا التبويب',
  stop: 'إيقاف',
  takenOver: 'وكيلك يستخدم الآن تبويبًا آخر.',
  lost: 'انقطع الاتصال بوكيلك. نحاول مجددًا…',
  explain:
    'يستطيع وكيل الذكاء الاصطناعي الخاص بك، الموصول بمفتاح وكيل، تشغيل الأوامر في هذا الجهاز وتعديل ملفات المستوى والضغط على «تحقّق من عملي». يُذكر هنا كل ما يفعله.',
  setUp: 'اضبط وكيلك',
  did: { run: 'شغّل', read_file: 'قرأ', write_file: 'كتب', list_files: 'عرض محتوى', check: 'ضغط «تحقّق من عملي»', open: 'فتح', status: 'اطّلع على المستوى', levels: 'اطّلع على قائمة المستويات' } as Record<AgentDid, string>,

  title: 'استخدم وكيلك الخاص',
  intro:
    'أي وكيل ذكاء اصطناعي يتحدث MCP (مثل Claude Code و Codex و Cursor) يستطيع العمل في EngineerOS باسمك: حل المستويات والمحطات في تبويب توصله. هو يعمل باسمك، فالعمل عملك وأنت مسؤول عنه.',
  limits:
    'يستطيع المفتاح استخدام تبويب توصله. ولا يستطيع أبدًا تغيير حسابك أو الكتابة في الدردشة.',
  server: 'عنوان الخادم',
  forClaude: 'أضفه إلى Claude Code',
  measure: 'دعنا نقيس كم يكلّف (Claude Code)',
  measureHint: 'ضع هذا في ‎.claude/settings.json‎ داخل المجلد الذي تشغّل منه Claude Code لعملك على EngineerOS. ما دام لدى وكيلك مستوى أو محطة أو مهمة مفتوحة، يُبلغنا Claude Code بكل استدعاء للنموذج: النموذج وعدد الوحدات والكلفة، فيعرض المستوى ما أنفقه وكيلك وكيف تنفق أقل. لا نحتفظ بشيء آخر مما يرسله، ولا بطلباتك أبدًا، ونُسقط الاستخدام حين لا يكون شيء مفتوحًا.',
  nameLabel: 'لأي شيء هذا المفتاح',
  namePlaceholder: 'Claude Code على حاسوبي',
  make: 'أنشئ مفتاحًا',
  shownOnce: 'انسخ هذا المفتاح الآن، فلن يظهر مرة أخرى.',
  copy: 'نسخ',
  copied: 'نُسخ',
  revoke: 'إلغاء',
  revoked: (at: number) => `أُلغي في ${shortDate(new Date(at), 'ar')}`,
  made: (at: number) => `أُنشئ في ${shortDate(new Date(at), 'ar')}`,
  lastUsed: (at: number) => `آخر استخدام ${shortDate(new Date(at), 'ar')}`,
  neverUsed: 'لم يُستخدم بعد',
  none: 'لا مفاتيح بعد.',
  tooMany: (max: number) => `لديك ${max} مفاتيح. ألغِ واحدًا لتنشئ غيره.`,
  failed: 'لم ينجح ذلك. حاول مرة أخرى.',
  signIn: 'سجّل الدخول لتنشئ مفتاحًا.',
};

export const de: typeof en = {
  connect: 'Meinen Agent diesen Tab nutzen lassen',
  connecting: 'Dein Agent wird verbunden …',
  connected: 'Dein Agent kann diesen Tab nutzen',
  stop: 'Beenden',
  takenOver: 'Dein Agent nutzt jetzt einen anderen Tab.',
  lost: 'Die Verbindung zu deinem Agent ist weg. Neuer Versuch …',
  explain:
    'Dein eigener KI-Agent, verbunden mit einem Agent-Schlüssel, kann in dieser Maschine Befehle ausführen, die Dateien des Levels bearbeiten und „Meine Arbeit prüfen“ drücken. Alles, was er tut, steht hier.',
  setUp: 'Agent einrichten',
  did: { run: 'Ausgeführt', read_file: 'Gelesen', write_file: 'Geschrieben', list_files: 'Aufgelistet', check: '„Meine Arbeit prüfen“ gedrückt', open: 'Geöffnet', status: 'Level angesehen', levels: 'Levelliste angesehen' } as Record<AgentDid, string>,

  title: 'Deinen eigenen Agent nutzen',
  intro:
    'Jeder KI-Agent, der MCP spricht (Claude Code, Codex, Cursor und andere), kann in EngineerOS in deinem Namen arbeiten: Levels und Checkpoints in einem Tab lösen, den du verbindest. Er handelt als du, also stehst du für die Arbeit ein.',
  limits:
    'Ein Schlüssel kann einen verbundenen Tab nutzen. Er kann niemals dein Konto ändern oder im Chat schreiben.',
  server: 'Serveradresse',
  forClaude: 'Zu Claude Code hinzufügen',
  measure: 'Uns messen lassen, was es kostet (Claude Code)',
  measureHint: 'Leg das in .claude/settings.json in dem Ordner ab, aus dem du Claude Code für EngineerOS startest. Solange dein Agent ein Level, einen Checkpoint oder eine Aufgabe offen hat, meldet Claude Code uns jeden Modellaufruf: das Modell, die Token-Zahlen und die Kosten. So zeigt das Level, was dein Agent ausgegeben hat und wie es günstiger geht. Sonst behalten wir nichts von dem, was es sendet, nie deine Prompts, und Nutzung, während nichts offen ist, verwerfen wir.',
  nameLabel: 'Wofür der Schlüssel ist',
  namePlaceholder: 'Claude Code auf meinem Laptop',
  make: 'Schlüssel erstellen',
  shownOnce: 'Kopiere diesen Schlüssel jetzt. Er wird nicht noch einmal angezeigt.',
  copy: 'Kopieren',
  copied: 'Kopiert',
  revoke: 'Widerrufen',
  revoked: (at: number) => `Widerrufen am ${shortDate(new Date(at), 'de')}`,
  made: (at: number) => `Erstellt am ${shortDate(new Date(at), 'de')}`,
  lastUsed: (at: number) => `zuletzt genutzt am ${shortDate(new Date(at), 'de')}`,
  neverUsed: 'noch nicht genutzt',
  none: 'Noch keine Schlüssel.',
  tooMany: (max: number) => `Du hast ${max} Schlüssel. Widerrufe einen, um einen neuen zu erstellen.`,
  failed: 'Das hat nicht geklappt. Versuch es noch einmal.',
  signIn: 'Melde dich an, um einen Schlüssel zu erstellen.',
};

export const ja: typeof en = {
  connect: 'このタブをエージェントに使わせる',
  connecting: 'エージェントに接続しています…',
  connected: 'エージェントがこのタブを使えます',
  stop: '停止',
  takenOver: 'エージェントは別のタブを使っています。',
  lost: 'エージェントとの接続が切れました。再接続しています…',
  explain:
    'エージェントキーで接続したあなた自身の AI エージェントが、このマシンでコマンドを実行し、レベルのファイルを編集し、「作業をチェック」を押せます。エージェントの操作はすべてここに表示されます。',
  setUp: 'エージェントを設定する',
  did: { run: '実行', read_file: '読み込み', write_file: '書き込み', list_files: '一覧', check: '「作業をチェック」を押しました', open: '開きました', status: 'レベルを確認しました', levels: 'レベル一覧を確認しました' } as Record<AgentDid, string>,

  title: '自分のエージェントを使う',
  intro:
    'MCP に対応した AI エージェント（Claude Code、Codex、Cursor など）は、あなたとして EngineerOS で作業できます。接続したタブでのレベルとチェックポイントです。エージェントはあなたとして動くので、その作業の責任はあなたにあります。',
  limits:
    'キーでできるのは、接続したタブの利用です。アカウントの変更、チャットへの投稿は決してできません。',
  server: 'サーバーのアドレス',
  forClaude: 'Claude Code に追加する',
  measure: 'コストを測定する（Claude Code）',
  measureHint: 'EngineerOS用にClaude Codeを起動するフォルダーの .claude/settings.json にこれを置きます。エージェントがレベル、チェックポイント、タスクを開いている間、Claude Codeはモデル呼び出しごとにモデル、トークン数、コストを私たちに報告します。これでレベルに、エージェントが使った額と節約の方法が表示されます。送られてくるそれ以外の情報は保存せず、プロンプトも一切保存しません。何も開いていない間の利用は破棄します。',
  nameLabel: 'キーの用途',
  namePlaceholder: 'ノートパソコンの Claude Code',
  make: 'キーを作成',
  shownOnce: 'このキーを今コピーしてください。二度と表示されません。',
  copy: 'コピー',
  copied: 'コピーしました',
  revoke: '無効にする',
  revoked: (at: number) => `${shortDate(new Date(at), 'ja')} に無効化`,
  made: (at: number) => `${shortDate(new Date(at), 'ja')} に作成`,
  lastUsed: (at: number) => `最終使用 ${shortDate(new Date(at), 'ja')}`,
  neverUsed: '未使用',
  none: 'キーはまだありません。',
  tooMany: (max: number) => `キーが ${max} 個あります。新しく作るには 1 つ無効にしてください。`,
  failed: 'うまくいきませんでした。もう一度お試しください。',
  signIn: 'キーを作成するにはサインインしてください。',
};
